import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';
import logoDark from '@/assets/sportara-logo-dark.png';
import logoLight from '@/assets/sportara-logo-light.png';

interface LogoProps {
  variant?: 'auto' | 'light' | 'dark';
  className?: string;
  alt?: string;
}

/**
 * Sportara logo. In `auto` mode (default) it follows the active in-app theme
 * via next-themes and swaps instantly when the user toggles light/dark.
 */
export function Logo({ variant = 'auto', className, alt = 'Sportara' }: LogoProps) {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // Shared image attributes ensure the logo stays crisp, centered, and
  // bitmap-clean across screen sizes and pixel densities.
  const baseClass =
    'block aspect-square object-contain object-center select-none [image-rendering:auto]';
  const merged = [baseClass, className].filter(Boolean).join(' ');
  const imgProps = {
    alt,
    className: merged,
    draggable: false as const,
    decoding: 'async' as const,
    loading: 'eager' as const,
  };

  if (variant === 'light') return <img src={logoLight} {...imgProps} />;
  if (variant === 'dark') return <img src={logoDark} {...imgProps} />;

  // auto — default to dark variant pre-mount to match SSR/initial dark theme
  const src = mounted && resolvedTheme === 'light' ? logoLight : logoDark;
  return <img src={src} {...imgProps} />;
}
